import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * OfflineBanner - tells the user the device lost its connection (bug 14).
 *
 * Before this, going offline looked like being signed out: /api/auth/me failed,
 * AuthContext treated the failure as a bad token and cleared it, and the driver
 * landed on the login screen in a truck with no signal. AuthContext now keeps
 * the session through a network failure, which leaves a second problem - the
 * app looks normal while every write silently fails. This says so out loud.
 *
 * Connectivity state comes from AuthContext rather than a second pair of
 * listeners here, so the banner and the silent auth re-check agree on when the
 * connection came back.
 *
 * Dismissal is per outage: dismissing hides the banner until the connection
 * returns, and the next time it drops the banner comes back.
 *
 * navigator.onLine only proves the device has *a* network, not that the API is
 * reachable, so this is a hint rather than a guarantee - which is why nothing
 * about auth depends on it.
 */
function OfflineBanner() {
  const { isOffline } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isOffline) setDismissed(false);
  }, [isOffline]);

  if (!isOffline || dismissed) return null;

  return (
    <div className="offline-banner" role="status" aria-live="polite">
      <span className="offline-banner-dot" aria-hidden="true" />
      <span className="offline-banner-text">
        You&rsquo;re offline. Some features may be unavailable.
      </span>
      <button
        type="button"
        className="offline-banner-dismiss"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss offline notice"
      >
        &times;
      </button>

      <style>{`
        .offline-banner {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 10px 44px 10px 16px;
          background: #b45309;
          color: #fff;
          font-size: 0.9rem;
          font-weight: 500;
          line-height: 1.3;
          text-align: center;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
          /* Clears the notch on a standalone iOS launch, where the banner
             would otherwise sit under the status bar. */
          padding-top: calc(10px + env(safe-area-inset-top, 0px));
        }

        .offline-banner-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #fbbf24;
          flex-shrink: 0;
        }

        .offline-banner-dismiss {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: #fff;
          font-size: 1.4rem;
          line-height: 1;
          /* 44px square: the iOS minimum tap target. */
          min-width: 44px;
          min-height: 44px;
          cursor: pointer;
          opacity: 0.85;
        }

        .offline-banner-dismiss:hover {
          opacity: 1;
        }
      `}</style>
    </div>
  );
}

export default OfflineBanner;
